import React from 'react'

import { datadogRum } from '@datadog/browser-rum'
import type { Map } from 'immutable'
import { useDispatch, useSelector } from 'react-redux'
import { usePrevious } from 'react-use'

import type { RecipeCoreId, RecipeUUID } from '@library/menu-service'

import { actionTypes } from 'actions/actionTypes'
import * as trackingKeys from 'actions/trackingKeys'
import { useCollection, useCurrentCollectionId } from 'hooks/menu-collections'
import { useInStockRecipeIds } from 'hooks/menu-recipes/useInStockRecipeIds'
import {
  getBasketMenuId,
  getBasketMenuUuid,
  getBasketRecipes,
  getBasketSlotId,
  getNumPortions,
} from 'selectors/basket'
import { getUTMAndPromoCode } from 'selectors/tracking'
import { type BasketRecipe, getBasketRecipeQuantity } from 'utils/basket'

import { useRecipeLimitReached } from '../limits'

const datadogActionByView: Record<string, string> = {
  view_recipe_list_screen: 'add_recipe_to_basket_via_recipe_grid',
  view_recipe_detail_screen: 'add_recipe_to_basket_via_recipe_details',
  menu_search_screen: 'add_recipe_to_basket_via_search',
}

export type AddRecipeInfo = {
  position?: string
  source?: 'merch_banner'
  merch_header_type?: 'campaign-banner' | 'compact-campaign-banner'
  promotionId?: string
}

export const useAddValidRecipeToBasket = () => {
  const basketRecipes = useSelector(getBasketRecipes)
  const slotId = useSelector(getBasketSlotId)
  const { promoCode, UTM } = useSelector(getUTMAndPromoCode)
  const currentMenuId = useSelector(getBasketMenuId)
  const currentMenuUUID = useSelector(getBasketMenuUuid)
  const basketNumPortions = useSelector(getNumPortions)

  const currentCollectionId = useCurrentCollectionId()
  const currentCollection = useCollection(currentCollectionId)
  const previousBasketRecipes = usePrevious(basketRecipes)
  const inStockRecipes = useInStockRecipeIds()

  const reachedLimit = useRecipeLimitReached(
    basketRecipes as unknown as Map<RecipeCoreId, BasketRecipe>,
  )

  const dispatch = useDispatch()

  return React.useCallback(
    (
      recipeId: RecipeUUID,
      view?: string,
      recipeInfo?: AddRecipeInfo,
      orderId?: string,
      isOrderRecipe?: boolean, // if this is true, the recipe was already part of the order and we are just initialising the basket
      numPortions?: number,
    ) => {
      const outOfStock = !inStockRecipes.has(recipeId)

      const indexOfCollection = currentCollection?.index ?? -1

      if ((reachedLimit || outOfStock) && !isOrderRecipe) {
        return
      }

      if (recipeInfo) {
        Object.assign(recipeInfo, { collection: currentCollectionId })
      }
      const recipePortionCount = numPortions ?? basketNumPortions

      dispatch({
        type: actionTypes.BASKET_RECIPE_ADD,
        recipeId,
        ...recipeInfo,
        ...(numPortions !== undefined && { numPortions }),
        isOrderRecipe,
        orderId,
        trackingData: view
          ? {
              actionType: trackingKeys.addRecipe,
              recipe_uuid: recipeId,
              recipe_portion_count: recipePortionCount,
              menu_id: currentMenuId,
              menu_uuid: currentMenuUUID,
              event_screen: view,
              collection_id: currentCollectionId,
              ...(currentCollection?.slug && { collection_slug: currentCollection.slug }),
              collection_position: indexOfCollection + 1,
              recipe_position: recipeInfo && recipeInfo.position,
              ...(recipeInfo?.source && { source: recipeInfo.source }),
              ...(recipeInfo?.merch_header_type && {
                merch_header_type: recipeInfo.merch_header_type,
              }),
              ...(recipeInfo?.promotionId && { promotion_id: recipeInfo.promotionId }),
              basket_recipe_count: basketRecipes.size + 1, // The action is performed in the same time so the size is not updated yet
            }
          : undefined,
      })

      if (reachedLimit) {
        dispatch({
          type: actionTypes.BASKET_LIMIT_REACHED,
          limitReached: reachedLimit,
          trackingData: {
            actionType: trackingKeys.basketLimit,
            limitReached: reachedLimit,
            view,
            source: actionTypes.RECIPE_ADDED,
          },
        })
      }

      let recipesCount = 0

      basketRecipes.forEach((entry) => {
        recipesCount += getBasketRecipeQuantity(entry)
      })

      const datadogAction = view ? datadogActionByView[view] : undefined
      if (datadogAction) {
        datadogRum.addAction(datadogAction, {
          recipesCount,
          menuId: currentMenuUUID,
          recipeId: recipeId,
        })
      }

      if (previousBasketRecipes && previousBasketRecipes.size < 2 && recipesCount > 1 && slotId) {
        dispatch({
          type: actionTypes.BASKET_ELIGIBLE_TRACK,
          trackingData: {
            actionType: trackingKeys.basketEligible,
            ...UTM,
            promoCode,
            menuRecipes: basketRecipes,
          },
        })
      }
    },
    [
      inStockRecipes,
      currentCollection?.index,
      reachedLimit,
      dispatch,
      currentMenuId,
      currentMenuUUID,
      currentCollectionId,
      currentCollection?.slug,
      basketRecipes,
      basketNumPortions,
      previousBasketRecipes,
      slotId,
      UTM,
      promoCode,
    ],
  )
}
